import { queue } from 'async';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

interface RateLimiterContextType {
  wrapApiCall: <T>(asyncFn: () => Promise<T>) => Promise<T>;
}

const RateLimiterContext = createContext<RateLimiterContextType | null>(null);

interface RateLimiterProviderProps {
  children: ReactNode;
  requestsPerSecond?: number;
}

interface ApiTask<T> {
  asyncFn: () => Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
}

export function RateLimiterProvider({
  children,
  requestsPerSecond = 5,
}: RateLimiterProviderProps) {
  const requestsPerSecondRef = useRef(requestsPerSecond);

  useEffect(() => {
    requestsPerSecondRef.current = requestsPerSecond;
  }, [requestsPerSecond]);

  const apiQueue = useMemo(
    () =>
      queue<ApiTask<any>>((task, callback) => {
        task
          .asyncFn()
          .then(task.resolve)
          .catch(task.reject)
          .finally(() => {
            setTimeout(callback, 1000 / requestsPerSecondRef.current);
          });
      }, 1),
    []
  );

  const wrapApiCall = useCallback(
    <T,>(asyncFn: () => Promise<T>): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        apiQueue.push({ asyncFn, resolve, reject });
      }),
    [apiQueue]
  );

  const contextValue = useMemo(() => ({ wrapApiCall }), [wrapApiCall]);

  return (
    <RateLimiterContext.Provider value={contextValue}>
      {children}
    </RateLimiterContext.Provider>
  );
}

export function useRateLimiter() {
  const context = useContext(RateLimiterContext);
  if (context === null) {
    throw new Error('useRateLimiter must be used within a RateLimiterProvider');
  }
  return context;
}
